import { gql } from 'graphql-request';
import { AxiosRequestConfig } from 'axios';
import { getGraphQLQueryClient } from './query-client';
import axiosClient from './request-client';
import type { Nullable, UserWithToken, WithData } from './api.types';

/**
 * Playmats del campo en los videos de replays, uno por torneo. Cada uno es una
 * mitad del campo (la plantilla, 4074×1959 px): el fondo, que el overlay
 * oscurece, y las zonas con transparencia (opcional), que van a brillo pleno.
 */
export type Playmat = {
  id: number;
  name: string;
  image: string;
  imageUrl: string;
  zonesImage: Nullable<string>;
  zonesImageUrl: Nullable<string>;
  tournamentsCount: number;
};

const PLAYMAT_FIELDS = gql`
  fragment PlaymatFields on Playmat {
    id
    name
    image
    imageUrl
    zonesImage
    zonesImageUrl
    tournamentsCount
  }
`;

/** La biblioteca, por nombre. Admin. */
export const getPlaymats = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<Playmat[]>>(
    gql`
      query playmats {
        data: playmats {
          ...PlaymatFields
        }
      }
      ${PLAYMAT_FIELDS}
    `
  );

/**
 * Alta de un playmat: nombre, fondo y, opcional, las zonas. Va por REST porque
 * es multipart y `/graphql` sólo acepta JSON. Admin.
 */
export const uploadPlaymat = (
  name: string,
  image: File,
  zones: Nullable<File>,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('image', image);
  if (zones) formData.append('zones', zones);
  return axiosClient.post('/playmats', formData, {
    ...config,
    headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
  });
};

export const renamePlaymat = async (
  user: Nullable<UserWithToken>,
  id: number,
  name: string
) =>
  getGraphQLQueryClient(user).request<WithData<Playmat>>(
    gql`
      mutation renamePlaymat($id: Int!, $name: String!) {
        data: renamePlaymat(id: $id, name: $name) {
          ...PlaymatFields
        }
      }
      ${PLAYMAT_FIELDS}
    `,
    { id, name }
  );

/** Los torneos que lo usaban quedan sin playmat. */
export const deletePlaymat = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deletePlaymat($id: Int!) {
        data: deletePlaymat(id: $id)
      }
    `,
    { id }
  );
